/** Sidebar AI Triage page local tabs (host-scoped). */
export const AI_TRIAGE_PAGE_TABS = {
  domainUrls: 'domainUrls',
  preferences: 'preferences',
  whitelistedDomains: 'whitelistedDomains',
};

/**
 * AiTriage — user-selectable level per backend-defined dimension (Low / Medium / High).
 *
 * ## Backend ↔ frontend contract
 *
 * **GET** (e.g. `GET /urls/:id/ai-triage` or nested in `GET /urls/:id`) returns:
 * - `config_items`: **list of dimensions** the user may tune (from DB/admin; not free-typed by user).
 * - `saved_item_levels` (optional): current choices `{ [itemId]: 'LOW'|'MEDIUM'|'HIGH' }` for this scope (url or domain).
 *
 * **PUT** (scope resolved server-side from this URL):
 * - `PUT /urls/:id/ai-triage/preferences` — `{ "itemLevels": { "…": "LOW"|"MEDIUM"|"HIGH" } }`
 * - `PUT /urls/:id/ai-triage/prompt` — `{ "draft_content"?: string, "versions"?: array }` (send at least one)
 *
 * **Database (typical):**
 * - **Catalog table** `ai_triage_dimension` (`id`, `key`, `label`, `description`, `sort_order`, `active`) — defines what appears in the UI.
 * - **Per scope** `ai_triage_preference` (`scope_type` enum url|domain, `scope_id`, `dimension_key`, `level` enum LOW|MEDIUM|HIGH) — unique (`scope`, `dimension_key`).
 * - Or one **JSONB** column `item_levels` on `url` / `domain` if cardinality is small.
 *
 * Version `config_schema_version` when you add/remove dimensions so clients can refresh.
 */

/** User-facing triage strength; persist these exact strings in DB. */
export const AI_TRIAGE_LEVEL_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

/** System / developer prompts for the triage model are defined server-side only — not shown in this UI. */

/**
 * Offline / pre-API fallback — keep keys in sync with `weborion-monitor-api/config/ai_triage.php`.
 * Prefer `webpage.ai_triage_config_items` from GET /urls/:id (includes `default_level` per field).
 */
export const AI_TRIAGE_CONFIG_ITEMS_EXAMPLE = [
  {
    id: 'ai_triage',
    label: 'AI Triage',
    description: 'AI Triage',
    default_level: 'MEDIUM',
    badge: null,
  },
  {
    id: 'popup_alert',
    label: 'Popup alert',
    description: 'Show a modal alert to users on page load or session start.',
    default_level: 'MEDIUM',
    badge: 'suggested',
  },
  {
    id: 'maintenance_announcement',
    label: 'Maintenance announcement',
    description: 'Scheduled downtime or degraded service warning banner.',
    default_level: 'MEDIUM',
    badge: 'suggested',
  },
  {
    id: 'cookie_consent',
    label: 'Cookie consent',
    description: 'Bottom banner with Accept all / Manage options.',
    default_level: 'LOW',
    badge: 'suggested',
  },
];
